import { useEffect, useState, type CSSProperties } from 'react';
import {
  Badge,
  Button,
  Caption1,
  Card,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Spinner,
  Switch,
  Text,
  tokens,
} from '@fluentui/react-components';
import { CopyRegular } from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import { PageHeader } from '../components/PageHeader';
import { UserPicker } from '../features/users/UserPicker';
import type {
  CredentialActionResult,
  CredentialOperation,
  DirectoryUserDto,
  UserDetailDto,
} from '../api/types';
import './credentials.css';

const OPERATIONS: { value: CredentialOperation; label: string }[] = [
  { value: 'ResetPassword', label: 'Passwort' },
  { value: 'ResetMfa', label: 'MFA' },
  { value: 'IssueTap', label: 'Access Pass' },
];

const CONFIRM_LABEL: Record<CredentialOperation, string> = {
  ResetPassword: 'Passwort zurücksetzen',
  ResetMfa: 'MFA zurücksetzen',
  IssueTap: 'Access Pass ausstellen',
};

const WARNINGS: Record<CredentialOperation, string> = {
  ResetPassword:
    'Setzt ein neues temporäres Passwort. Der Nutzer muss es bei der nächsten Anmeldung ändern; aktive Sitzungen bleiben ggf. bestehen.',
  ResetMfa:
    'Entfernt alle registrierten MFA-Methoden (Authenticator, Telefon, FIDO2, Windows Hello, …). Der Nutzer muss MFA neu einrichten — bis dahin kann die Anmeldung blockiert sein.',
  IssueTap:
    'Stellt einen zeitlich begrenzten Zugangspass aus, der MFA vorübergehend ersetzt. Nur an die verifizierte Person weitergeben.',
};

const TAP_LIFETIMES = [
  { minutes: 60, label: '1 Stunde' },
  { minutes: 240, label: '4 Stunden' },
  { minutes: 480, label: '8 Stunden' },
  { minutes: 1440, label: '1 Tag' },
];

const panelStyle: CSSProperties = {
  maxWidth: '640px',
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacingVerticalL,
};

const cardStyle: CSSProperties = {
  padding: tokens.spacingVerticalL,
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacingVerticalS,
};

const title = 'Passwort & MFA';

export function CredentialsPage() {
  const api = useApi();
  const policies = useAsync(() => api.getPagePolicies(), [api]);
  const ticketMode = policies.data?.find((p) => p.pageKey === 'users.credentials')?.ticketMode ?? 'None';

  const [operation, setOperation] = useState<CredentialOperation>('ResetPassword');
  const [selected, setSelected] = useState<DirectoryUserDto | undefined>();
  const [detail, setDetail] = useState<UserDetailDto | undefined>();
  const [detailLoading, setDetailLoading] = useState(false);
  const [ticket, setTicket] = useState('');
  const [ticketError, setTicketError] = useState<string | undefined>();
  const [tapLifetime, setTapLifetime] = useState(60);
  const [tapOneTime, setTapOneTime] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CredentialActionResult | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!selected) {
      setDetail(undefined);
      return;
    }
    let active = true;
    setDetailLoading(true);
    api
      .getUser(selected.id)
      .then((d) => {
        if (active) setDetail(d);
      })
      .catch(() => {
        if (active) setDetail(undefined);
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });
    return () => {
      active = false;
    };
  }, [selected, api]);

  // Ergebnis/Fehler bei Wechsel von Aktion oder Person verwerfen (Benutzer bleibt gewählt).
  useEffect(() => {
    setResult(null);
    setPending(false);
    setError(null);
  }, [operation, selected]);

  function resetAll() {
    setSelected(undefined);
    setDetail(undefined);
    setTicket('');
    setTicketError(undefined);
    setResult(null);
    setPending(false);
    setError(null);
  }

  async function confirm() {
    if (!selected) return;
    if (ticketMode === 'Required' && !ticket.trim()) {
      setTicketError('Ticketnummer ist erforderlich.');
      return;
    }
    setTicketError(undefined);
    setBusy(true);
    setError(null);
    try {
      const res = await api.resetCredentials(selected.id, {
        operation,
        ticketNumber: ticket.trim() || undefined,
        tapLifetimeMinutes: operation === 'IssueTap' ? tapLifetime : undefined,
        tapOneTime: operation === 'IssueTap' ? tapOneTime : undefined,
      });
      if (res.status === 'PendingApproval') setPending(true);
      else if (res.result) setResult(res.result);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (pending) {
    return (
      <div>
        <PageHeader title={title} />
        <div style={panelStyle}>
          <MessageBar intent="info">
            <MessageBarBody>
              <MessageBarTitle>Antrag eingereicht</MessageBarTitle>
              Diese Aktion ist freigabepflichtig. Nach der Freigabe erscheint das Ergebnis — bei Passwort-Reset und
              Access Pass auch das Geheimnis — unter „Meine Anfragen".
            </MessageBarBody>
          </MessageBar>
          <div>
            <Button appearance="primary" onClick={resetAll}>
              Weitere Aktion
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div>
        <PageHeader title={title} />
        <CredentialResultView result={result} onReset={resetAll} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={title}
        description="Danger-Zone: Passwort- oder MFA-Reset und Temporary Access Pass ausstellen."
      />
      <div style={panelStyle}>
        <div className="cred-seg" role="radiogroup" aria-label="Aktion">
          {OPERATIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={o.value === operation}
              className={o.value === operation ? 'cred-seg-item selected' : 'cred-seg-item'}
              onClick={() => setOperation(o.value)}
            >
              {o.label}
            </button>
          ))}
        </div>

        {error ? (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        ) : null}

        <Field label="Benutzer">
          <UserPicker onSelect={setSelected} />
        </Field>

        {detailLoading ? <Spinner size="tiny" label="Lade Details…" /> : null}

        {detail ? (
          <Card style={cardStyle}>
            <Text weight="semibold" className="pii">
              {detail.displayName}
            </Text>
            <Caption1 className="pii">{detail.userPrincipalName}</Caption1>
            <div style={{ display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center' }}>
              <Caption1>Status:</Caption1>
              <Badge appearance="tint" color={detail.accountEnabled ? 'success' : 'danger'}>
                {detail.accountEnabled ? 'aktiv' : 'gesperrt'}
              </Badge>
              {detail.jobTitle ? <Caption1>· {detail.jobTitle}</Caption1> : null}
            </div>
            <MessageBar intent="warning">
              <MessageBarBody>{WARNINGS[operation]}</MessageBarBody>
            </MessageBar>
          </Card>
        ) : null}

        {operation === 'IssueTap' ? (
          <div style={{ display: 'flex', gap: tokens.spacingHorizontalL, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Field label="Gültigkeit">
              <Dropdown
                style={{ minWidth: '180px' }}
                value={TAP_LIFETIMES.find((l) => l.minutes === tapLifetime)?.label ?? ''}
                selectedOptions={[String(tapLifetime)]}
                onOptionSelect={(_, d) => d.optionValue && setTapLifetime(Number(d.optionValue))}
              >
                {TAP_LIFETIMES.map((l) => (
                  <Option key={l.minutes} value={String(l.minutes)} text={l.label}>
                    {l.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            <Switch checked={tapOneTime} onChange={(_, d) => setTapOneTime(d.checked)} label="Nur einmalig nutzbar" />
          </div>
        ) : null}

        {ticketMode !== 'None' ? (
          <Field
            label="Ticketnummer"
            required={ticketMode === 'Required'}
            validationMessage={ticketError}
            hint={ticketMode === 'Required' ? 'Pflicht für diese Aktion.' : 'Optional.'}
          >
            <Input value={ticket} onChange={(_, d) => setTicket(d.value)} placeholder="z. B. INC0012345" />
          </Field>
        ) : null}

        <div>
          <Button
            appearance="primary"
            style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}
            disabled={!detail || busy}
            onClick={confirm}
          >
            {busy ? 'Bitte warten…' : CONFIRM_LABEL[operation]}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CredentialResultView({ result, onReset }: { result: CredentialActionResult; onReset: () => void }) {
  const [copied, setCopied] = useState(false);

  function copySecret() {
    if (!result.secret) return;
    navigator.clipboard
      .writeText(result.secret)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  }

  const hint =
    result.operation === 'ResetPassword'
      ? 'Muss bei der ersten Anmeldung geändert werden.'
      : result.operation === 'IssueTap'
        ? `Gültig für ${formatLifetime(result.lifetimeMinutes)}${result.oneTime ? ' · nur einmalig nutzbar' : ''}.`
        : undefined;

  return (
    <div style={panelStyle}>
      <MessageBar intent="success">
        <MessageBarBody>
          <MessageBarTitle>Erledigt</MessageBarTitle>
          {result.summary}
        </MessageBarBody>
      </MessageBar>

      {result.secret ? (
        <Card style={cardStyle}>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{result.secretLabel}</Caption1>
          <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
            <Text font="monospace" size={400}>
              {result.secret}
            </Text>
            <Button size="small" appearance="subtle" icon={<CopyRegular />} onClick={copySecret}>
              {copied ? 'Kopiert' : 'Kopieren'}
            </Button>
          </div>
          {hint ? <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{hint}</Caption1> : null}
        </Card>
      ) : null}

      <div>
        <Button appearance="primary" onClick={onReset}>
          Weitere Aktion
        </Button>
      </div>
    </div>
  );
}

function formatLifetime(minutes?: number): string {
  if (!minutes) return 'begrenzte Zeit';
  if (minutes % 1440 === 0) return `${minutes / 1440} Tag(e)`;
  if (minutes % 60 === 0) return `${minutes / 60} Stunde(n)`;
  return `${minutes} Minuten`;
}
